import { createContext, useContext, useMemo, useRef } from 'react';

import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import translation from 'src/components/translation';

import useHandlers from 'src/components/VacancyCreate/contexts/StepsContext/hooks/useHandlers';
import useScroll from 'src/components/VacancyCreate/contexts/StepsContext/hooks/useScroll';
import useSteps from 'src/components/VacancyCreate/contexts/StepsContext/hooks/useSteps';
import { CombinerStep } from 'src/components/VacancyCreate/contexts/StepsContext/hooks/useStepsCombiner';
import { Step, StepName } from 'src/components/VacancyCreate/contexts/StepsContext/types';

interface StepsContextValues {
    steps: Step[];
    isStandaloneStepEnabled: boolean;
    standaloneStepName: StepName | null;
    currentStepIndex: number;
    activeStepIndex: number;
    texts: {
        pageTitle: string | null;
        pageTitleHint: string | null;
    };
    renderedFieldsRef: React.MutableRefObject<Map<string, { stepName: StepName }>>;
    forceShowAllSteps: () => void;
    openNextStep: () => void;
    openStep: (stepIndex: number, withScroll?: boolean) => void;
    setActiveStepIndex: (stepIndex: number) => void;
}

const StepsContext = createContext<StepsContextValues>({
    steps: [],
    isStandaloneStepEnabled: false,
    standaloneStepName: null,
    currentStepIndex: 0,
    activeStepIndex: 0,
    texts: {
        pageTitle: '',
        pageTitleHint: null,
    },
    renderedFieldsRef: { current: new Map() },
    forceShowAllSteps: () => null,
    openNextStep: () => null,
    openStep: () => null,
    setActiveStepIndex: () => null,
});

const TrlKeys = {
    pageTitle: {
        // Пустые переводы у шагов-порятнок
        [StepName.MainInfo]: '',
        [StepName.Contacts]: '',
        [StepName.PublicationType]: 'employer.vacancyCreate.pageTitle.publicationType',
    },
};

interface StepsContextProviderProps {
    showAllNotStandaloneSteps?: boolean;
    pageTitle?: string;
    pageTitleHint?: string;
    createSteps: () => CombinerStep[];
    onOpenNextStep?: (currentStepIndex: number, nextStepIndex: number) => void;
}

const StepsContextProvider: TranslatedComponent<StepsContextProviderProps & React.PropsWithChildren> = ({
    showAllNotStandaloneSteps,
    pageTitle,
    pageTitleHint = null,
    trls,
    createSteps,
    onOpenNextStep,
    children,
}) => {
    const {
        steps,
        isStandaloneStepEnabled,
        standaloneStepName,
        currentStepIndex,
        activeStepIndex,
        forceShowAllSteps,
        setCurrentStepIndex,
        setActiveStepIndex,
        recreateSteps,
    } = useSteps({
        showAllNotStandaloneSteps,
        createSteps,
    });

    const { scrollToStep, openNextStep, openStep } = useHandlers({
        steps,
        currentStepIndex,
        activeStepIndex,
        setCurrentStepIndex,
        setActiveStepIndex,
        recreateSteps,
        onOpenNextStep,
    });

    useScroll({ steps, currentStepIndex, scrollToStep });

    const texts = useMemo(
        () => ({
            pageTitle:
                (standaloneStepName && TrlKeys.pageTitle[standaloneStepName]
                    ? trls?.[TrlKeys.pageTitle[standaloneStepName]]
                    : pageTitle) || null,
            pageTitleHint: standaloneStepName ? null : pageTitleHint,
        }),
        [pageTitle, pageTitleHint, standaloneStepName, trls]
    );

    const renderedFieldsRef = useRef(new Map<string, { stepName: StepName }>());

    const value = useMemo(
        () => ({
            steps,
            isStandaloneStepEnabled,
            standaloneStepName,
            currentStepIndex,
            activeStepIndex,
            texts,
            renderedFieldsRef,
            forceShowAllSteps,
            openNextStep,
            openStep,
            setActiveStepIndex,
        }),
        [
            steps,
            isStandaloneStepEnabled,
            standaloneStepName,
            currentStepIndex,
            activeStepIndex,
            texts,
            renderedFieldsRef,
            forceShowAllSteps,
            openNextStep,
            openStep,
            setActiveStepIndex,
        ]
    );

    return <StepsContext.Provider value={value}>{children}</StepsContext.Provider>;
};

export default translation(StepsContextProvider);

export const useStepsContext = (): StepsContextValues => useContext<StepsContextValues>(StepsContext);
